<script lang="ts">
export default {
  name: "FrontendApp",
};
</script>

<script lang="ts" setup>
import FrontendNavigation from "@/frontend/components/Navigation/index.vue";
import AppNavigationHeader from "@/shared/components/AppNavigation/Header/index.vue";
import HomeSearch from "@/frontend/components/Home/Search/index.vue";
import FrontendNavigationMobile from "@/frontend/components/Navigation/Mobile/index.vue";
import AppFooter from "@/shared/components/AppFooter/index.vue";
import SupportBtn from "@/frontend/components/SupportBtn/index.vue";
import HangarSyncProgressStack from "@/frontend/components/Hangar/SyncProgressStack/index.vue";
import CartWidget from "@/frontend/components/Cart/Widget/index.vue";
import { useFleetStore } from "@/frontend/stores/fleet";
import AppEnvironment from "@/shared/components/AppEnvironment/index.vue";
import AppModal from "@/shared/components/AppModal/index.vue";
import OffCanvas from "@/shared/components/OffCanvas/index.vue";
import AppConfirm from "@/shared/components/AppConfirm/index.vue";
import AppNotifications from "@/shared/components/AppNotifications/index.vue";
import SecurePage from "@/frontend/components/core/SecurePage/index.vue";
import BackgroundImage from "@/shared/components/BackgroundImage/index.vue";
import { useI18n } from "@/shared/composables/useI18n";
import { useUpdates } from "@/frontend/composables/useUpdates";
import { useAppStore } from "@/frontend/stores/app";
import { useNavStore } from "@/shared/stores/nav";
import { useOverlayStore } from "@/shared/stores/overlay";
import { useI18nStore } from "@/shared/stores/i18n";
import { useSessionStore } from "@/frontend/stores/session";
import { storeToRefs } from "pinia";
import { type RouteLocationNormalizedLoaded, useRoute } from "vue-router";
import { pageKey as routePageKey } from "@/frontend/utils/pageKey";
import { useMobile } from "@/shared/composables/useMobile";
import { useComlink } from "@/shared/composables/useComlink";
import { useAhoy } from "@/frontend/composables/useAhoy";
import { useMe as useMeQuery } from "@/services/fyApi";
import { useQueryClient } from "@tanstack/vue-query";
import FetchProgressBar from "@/shared/components/FetchProgressBar/index.vue";
import PullToRefresh from "@/shared/components/PullToRefresh/index.vue";
import { BtnVariantsEnum } from "@/shared/components/base/Btn/types";
import { useAxiosInterceptors } from "@/frontend/composables/useAxiosInterceptors";
import { useCheckStoreVersion } from "@/shared/composables/useCheckStoreVersion";
import { useAppUpdateNotice } from "@/shared/composables/useAppUpdateNotice";
import { useChunkFailureNotice } from "@/shared/composables/useChunkFailureNotice";
import { useAppNotifications } from "@/shared/composables/useAppNotifications";
import { useWebpCheck } from "@/shared/composables/useWebpCheck";
import { useFlashNotifications } from "@/shared/composables/useFlashNotifications";
import { useMetaInfo } from "@/shared/composables/useMetaInfo";
import { useSupportPrompt } from "@/shared/composables/useSupportPrompt";
import { useWebPush } from "@/shared/composables/useWebPush";
import {
  useModalQuery,
  MODAL_QUERY_PARAM,
} from "@/frontend/composables/useModalQuery";

useWebpCheck(true);

// Outside the router-view, which remounts its page on every navigation: the
// fleet's setup tour walks across those pages. Loaded once a tour is asked for.
const FleetTour = defineAsyncComponent(
  () => import("@/frontend/components/Fleets/Tour/index.vue"),
);

const fleetStore = useFleetStore();

useMetaInfo();

useAxiosInterceptors();

const mobile = useMobile();

useUpdates();

useAhoy();

const { requestBrowserPermission } = useAppNotifications();

const appStore = useAppStore();

const navStore = useNavStore();

const { collapsed: navCollapsed } = storeToRefs(navStore);

const overlayStore = useOverlayStore();

const { visible: overlayVisible } = storeToRefs(overlayStore);

const sessionStore = useSessionStore();

const { isAuthenticated } = storeToRefs(sessionStore);

const supportPrompt = useSupportPrompt();

const { t, availableLocales, currentLocale } = useI18n();

useFlashNotifications();

watch(
  () => navCollapsed.value,
  () => {
    setNoScroll();
  },
);

watch(
  () => overlayVisible.value,
  () => {
    setNoScroll();
  },
);

const queryClient = useQueryClient();

const mainInner = ref<HTMLElement | null>(null);

/*
 * Installed as a PWA there is no reload button, and `refetchOnWindowFocus` is
 * off, so a page left open keeps showing what it had when it was opened. This
 * is the way back to fresh data: every mounted query is marked stale and the
 * active ones refetch, which is a reload of the page's data without paying for
 * the app shell again -- and it keeps the scroll position and the filters that
 * a document reload would throw away.
 */
const refreshPage = () => queryClient.invalidateQueries();

const router = useRouter();

// A renewal the worker could not save -- it runs without a session once the
// reader is logged out -- is saved on the next signed-in visit. Nothing is
// sent unless this browser subscribed for this account before.
const webPush = useWebPush();

const syncPushSubscription = () => {
  const userId = sessionStore.currentUser?.id;
  if (!userId) return;

  webPush.refresh(userId).catch(() => undefined);
};

watch(
  () => isAuthenticated.value,
  async () => {
    if (isAuthenticated.value) {
      await requestBrowserPermission();
      maybeCountLogin();
      syncPushSubscription();
    } else if (route.meta.needsAuthentication) {
      await router.push({ name: "login" });
    }
  },
);

const maybeCountLogin = () => {
  if (supportPrompt.sessionAlreadyCountedLogin()) return;
  supportPrompt.markLoginCountedForSession();
  supportPrompt.notifyIfMilestone("logins", [5, 10, 25, 50], "loginMilestone");
};

const maybeCountVisit = () => {
  if (isAuthenticated.value) return;
  if (supportPrompt.sessionAlreadyCountedVisit()) return;
  supportPrompt.markVisitCountedForSession();
  supportPrompt.notifyIfMilestone("visits", [10, 25, 50], "visitMilestone");
};

const route = useRoute();

const comlink = useComlink();

// The address names the modal that is open -- a link, a login coming back, or
// a button that wrote it there. The modal is the app's, so it is put up here
// rather than by whatever route the visitor happens to land on, and closing it
// takes the name back out.
const { openFromQuery: openModalFromQuery, clearModalQuery } = useModalQuery();

const forgetClosedModal = () => {
  void clearModalQuery();
};

watch(() => route.query[MODAL_QUERY_PARAM], openModalFromQuery);

watch(
  () => route.name,
  async () => {
    await checkSessionReload();

    setupLocale();
  },
);

useCheckStoreVersion(appStore, { checkOnResume: true });

useAppUpdateNotice(appStore);

useChunkFailureNotice();

const userUpdateComlink = ref();
const fleetCreateComlink = ref();
const fleetUpdateComlink = ref();

onMounted(async () => {
  await checkSessionReload();
  setNoScroll();

  openModalFromQuery();

  if (isAuthenticated.value) {
    await requestBrowserPermission();
    maybeCountLogin();
    syncPushSubscription();
  } else {
    maybeCountVisit();
  }

  userUpdateComlink.value = comlink.on("user-update", refetchCurrentUser);
  fleetCreateComlink.value = comlink.on("fleet-create", refetchCurrentUser);
  fleetUpdateComlink.value = comlink.on("fleet-update", refetchCurrentUser);

  setupLocale();
});

onUnmounted(() => {
  userUpdateComlink.value();
  fleetCreateComlink.value();
  fleetUpdateComlink.value();
});

const i18nStore = useI18nStore();

const { locale } = storeToRefs(i18nStore);

const setupLocale = () => {
  if (!locale.value && availableLocales().includes(navigator.language)) {
    i18nStore.locale = navigator.language;
  }
};

const setNoScroll = () => {
  if (!navCollapsed.value) {
    document.body.classList.add("nav-visible");
  } else {
    document.body.classList.remove("nav-visible");
  }

  if (!navCollapsed.value || overlayVisible.value) {
    document.body.classList.add("no-scroll");
  } else {
    document.body.classList.remove("no-scroll");
  }
};

const checkSessionReload = async () => {
  if (route.query.reload_session) {
    await sessionStore.fetchUserAndLogin();
  }
};

const { data: user, refetch: refetchCurrentUser } = useMeQuery({
  query: {
    retry: false,
  },
});

watch(
  () => user.value,
  () => {
    if (user.value) {
      sessionStore.login(user.value);
    }
  },
);

const localeMapping = {
  de: "Deutsch",
  en: "English",
  es: "Español",
  fr: "Français",
  it: "Italiano",
  zh: "中文",
  "zh-CN": "中文 (简体)",
  "zh-TW": "中文 (繁體)",
};

const locales = computed(() => {
  return availableLocales() as (keyof typeof localeMapping)[];
});

const activeLocale = (locale: string) => {
  return (
    locale === currentLocale() ||
    (!locale.includes("zh") && locale === currentLocale().split("-")[0])
  );
};

const pageKey = (viewRoute: RouteLocationNormalizedLoaded) =>
  `${locale.value}-${routePageKey(viewRoute)}`;

const setLocale = (locale: string) => {
  i18nStore.locale = locale;
};
</script>

<template>
  <div
    :key="locale"
    :class="{
      [`page-${String(route.name)}`]: true,
    }"
    class="app-body"
  >
    <!-- First, ahead of the page: pages teleport into its body, and on a
         locale change the whole tree mounts in one pass, in template order.
         It is fixed-position, so where it sits here changes nothing on screen. -->
    <OffCanvas />
    <FetchProgressBar />
    <PullToRefresh
      v-if="mobile"
      :target="mainInner"
      :refresh="refreshPage"
      :disabled="!navCollapsed || overlayVisible"
    />
    <BackgroundImage />

    <div class="app-content">
      <transition name="fade" mode="out-in">
        <FrontendNavigation />
      </transition>
      <div class="main-wrapper">
        <div
          id="main-content"
          ref="mainInner"
          class="main-inner"
          :class="{
            'main-inner--with-primary-action': route.meta.primaryAction,
          }"
        >
          <AppNavigationHeader>
            <template #left>
              <HomeSearch
                v-if="route.matched.length && !route.meta.hideHeaderSearch"
                compact
              />
            </template>
          </AppNavigationHeader>

          <router-view v-slot="{ Component, route: viewRoute }">
            <transition name="fade" mode="out-in">
              <SecurePage
                v-if="
                  viewRoute.meta.needsSecurityConfirm &&
                  !sessionStore.accessConfirmedDate
                "
              />
              <section
                v-else
                class="container main"
                :class="{
                  [route.name || '']: true,
                }"
              >
                <component :is="Component" :key="pageKey(viewRoute)" />
              </section>
            </transition>
          </router-view>
        </div>

        <AppFooter
          :codename="appStore.codename"
          :version="appStore.version"
          :git-revision="appStore.gitRevision"
          :online="appStore.online"
        >
          <!-- The `|` separators are gone: the row lays itself out with a gap,
               and separators were never the call site's content. -->
          <a
            v-tooltip="'Roberts Space Industries'"
            href="https://robertsspaceindustries.com/"
            target="_blank"
            rel="noopener"
          >
            RSI
          </a>
          <router-link :to="{ name: 'privacy-policy' }">
            {{ t("nav.privacyPolicy") }}
          </router-link>
          <router-link :to="{ name: 'impressum' }">
            {{ t("nav.impressum") }}
          </router-link>
          <a href="https://api.fleetyards.net" target="_blank" rel="noopener">
            {{ t("nav.api") }}
          </a>
          <BtnDropdown :expand-top="true" :variant="BtnVariantsEnum.BARE">
            <template #label>
              <i class="fa-duotone fa-language" /> {{ currentLocale() }}
            </template>
            <Btn
              v-for="availableLocale in locales"
              :key="`locale-${availableLocale}`"
              :active="activeLocale(availableLocale)"
              @click="setLocale(availableLocale)"
            >
              {{ localeMapping[availableLocale] }} - {{ availableLocale }}
            </Btn>
          </BtnDropdown>

          <!-- The support button lives here, not in AppFooter: it links to a
               frontend route, and the footer also renders in admin and docs. -->
          <template #actions>
            <SupportBtn :variant="BtnVariantsEnum.BARE">
              {{ t("labels.supportUs") }}
              <i class="fa fa-heart" />
            </SupportBtn>
          </template>
        </AppFooter>
      </div>
    </div>

    <transition name="fade" mode="out-in">
      <FrontendNavigationMobile v-if="mobile" />
    </transition>

    <AppConfirm />
    <AppModal @modal-closed="forgetClosedModal" />
    <AppNotifications />
    <FleetTour v-if="fleetStore.tourFleet" />
    <HangarSyncProgressStack />
    <CartWidget />
    <AppEnvironment :git-revision="appStore.gitRevision" />
  </div>
</template>
